import React from 'react';
import { Box, Button, Gap, Gutter, SavingTime, Text } from '@common/components';

export type ScheduleReviewSessionHeaderProps = {
  /** Shown small and muted above the unit name. */
  serviceLineName?: string;
  /** Unit identity is hidden unless a unit is selected. */
  unitName?: string;
  /** Shifts auto-filled by the balancer run. */
  assignmentsCount?: number;
  /** A save is in flight — shows "Saving…". */
  isSaving?: boolean;
  /** Last successful save; nothing is shown before the first save. */
  lastSavedAt?: Date | null;
  /** Opens the Activity tab to review the auto-applied changes. The link is hidden when absent. */
  onViewActivity?: () => void;
  /**
   * Approves the reviewed unit and finishes the session. Omitted when the run is no
   * longer awaiting review (the control is hidden — the page is then read-only).
   */
  onMarkComplete?: () => void;
};

/**
 * Persistent top bar of the Schedule Review session: unit identity, a summary of the
 * balancer's auto-filled shifts, save state, and the always-visible Mark Complete control.
 * The MVP has no suggestion-review flow yet — the balancer's moves are applied automatically,
 * so they are surfaced as "auto-filled", not as suggestions awaiting review.
 */
export function ScheduleReviewSessionHeader({
  serviceLineName,
  unitName,
  assignmentsCount = 0,
  isSaving = false,
  lastSavedAt = null,
  onViewActivity,
  onMarkComplete,
}: ScheduleReviewSessionHeaderProps) {
  const hasRun = assignmentsCount > 0;

  return (
    <div className="schedule-review-session-header">
      <Gutter asPadding direction="vertical" size="md">
        <Gutter asPadding direction="horizontal" size="lg">
          <Gap size="md" justify="space-between" align="center" fullWidth>
            <Gap size="md" align="center">
              {unitName && (
                <Gap vertical size="xs">
                  {serviceLineName && (
                    <Text size="sm" secondary block>
                      {serviceLineName}
                    </Text>
                  )}
                  <Text size="md" bold block>
                    {unitName}
                  </Text>
                </Gap>
              )}
              {hasRun && (
                <Box color="grassLightest">
                  <Gap size="sm" align="center">
                    <Text size="md" bold color="midnightDarkest">
                      {`${assignmentsCount} shift${
                        assignmentsCount === 1 ? '' : 's'
                      } auto-filled`}
                    </Text>
                    {onViewActivity && (
                      <Button kind="link" size="sm" onClick={onViewActivity}>
                        View auto-applied changes
                      </Button>
                    )}
                  </Gap>
                </Box>
              )}
            </Gap>

            <Gap size="md" align="center">
              <SavingTime saving={isSaving} lastSavedAt={lastSavedAt} />
              {/* Hidden once the run is no longer awaiting review — the page renders
                  read-only and there is nothing left to approve. */}
              {onMarkComplete && (
                <Button
                  kind="primary"
                  loading={isSaving}
                  onClick={onMarkComplete}
                >
                  Review &amp; Mark Complete
                </Button>
              )}
            </Gap>
          </Gap>
        </Gutter>
      </Gutter>
    </div>
  );
}
